/**
 * @component ry-checkbox
 * @category  form
 * @summary   Binary checkbox with indeterminate state. Wraps a hidden `<input type="checkbox">`. Vanilla form-control complement to `<ry-tile-checkbox>` (which is the large card variant).
 *
 * @attribute checked        boolean   default=false   Initial checked state.
 * @attribute indeterminate  boolean   default=false   Tri-state "mixed" indicator (renders as dash). Independent of checked.
 * @attribute disabled       boolean   default=false   Non-interactive.
 * @attribute name           string?                   Form field name on the inner input.
 * @attribute value          string    default="on"    Submitted value when checked. Matches HTML default.
 *
 * @property  checked        boolean   Mirrors `checked` attribute (get/set).
 * @property  indeterminate  boolean   Sets the inner input's indeterminate flag + reflects to host attribute.
 * @property  value          string    Mirrors the inner input's value.
 *
 * @event ry:beforechange
 *        detail: { value: string, checked: boolean, previous: boolean }
 *        fires:  from the inner input's `click` (mouse, touch, Space, label click) before the toggle commits. `checked` is what it is ABOUT to become, `previous` what it is now. An indeterminate box reports `checked: true` (the click clears indeterminate and checks).
 *        cancelable: preventDefault() cancels the inner input's click — the platform leaves `checked` and `indeterminate` untouched and `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { value: string, checked: boolean }
 *        fires:  user toggle (mouse, touch, Space). Setting `.checked` or `.indeterminate` programmatically is SILENT.
 *
 * @keyboard Space — toggles when focused. (Native input behavior.)
 *
 * @state    `data-ry-state="indeterminate"` is mirrored onto the host while indeterminate is true.
 *
 * @content  Inline text becomes the visible label next to the box.
 *
 * @dom
 *   ry-checkbox
 *     > label.ry-checkbox__wrapper
 *       > input.ry-checkbox__input        (visually hidden, type="checkbox")
 *       > span.ry-checkbox__box           (visual box)
 *         > svg.ry-checkbox__check        (check icon, shown when checked)
 *         > span.ry-checkbox__dash        (indeterminate dash, shown when indeterminate)
 *       > span.ry-checkbox__label         (only if textContent provided)
 *
 * @gotcha The visually-hidden input is `position: absolute`. The host element is
 *         `position: relative` (set in ry-structure.css) so the input anchors here.
 *
 * @gotcha Setting `.checked` or `.indeterminate` programmatically updates state
 *         SILENTLY — it does NOT fire `ry:change`. Mirror of the ry-switch contract.
 *         To force the event, dispatch a `change` event on the inner input.
 *
 * @gotcha `indeterminate` is independent of `checked`. Per HTML spec, an
 *         indeterminate checkbox can ALSO be checked — the visual just shows the
 *         dash instead of the check. A user click clears indeterminate AND toggles checked.
 *
 * @example simple
 * ```html
 * <ry-checkbox>I agree to the terms</ry-checkbox>
 * ```
 *
 * @example pre-checked
 * ```html
 * <ry-checkbox checked>Subscribe to updates</ry-checkbox>
 * ```
 *
 * @example indeterminate
 * ```html
 * <ry-checkbox indeterminate>Select all (some)</ry-checkbox>
 * ```
 *
 * @example disabled
 * ```html
 * <ry-checkbox disabled>Locked option</ry-checkbox>
 * <ry-checkbox checked disabled>Locked &amp; on</ry-checkbox>
 * ```
 *
 * @example no-label
 * ```html
 * <ry-checkbox></ry-checkbox>
 * ```
 *
 * @tutorial basic-toggle
 * ```js
 * const el = mount('<ry-checkbox>Agree</ry-checkbox>');
 * await expectEqual(el.checked, false, 'starts unchecked');
 *
 * const input = el.querySelector('input');
 * const toggle = () => {
 *   input.checked = !input.checked;
 *   input.dispatchEvent(new Event('change', { bubbles: true }));
 * };
 *
 * await expectEvent(el, 'ry:change', toggle, { checked: true });
 * await expectEqual(el.checked, true, 'first toggle on');
 *
 * await expectEvent(el, 'ry:change', toggle, { checked: false });
 * await expectEqual(el.checked, false, 'second toggle off');
 * ```
 *
 * @tutorial programmatic-setter
 * ```js
 * // .checked= setter mutates state but does NOT fire ry:change. Important.
 * const el = mount('<ry-checkbox>x</ry-checkbox>');
 * let fired = 0;
 * el.addEventListener('ry:change', () => fired++);
 *
 * el.checked = true;
 * await expectEqual(el.checked, true, 'setter updates state');
 * await expectAttr(el, 'checked', '', 'setter syncs attribute');
 * await expectEqual(fired, 0, 'setter does NOT fire ry:change');
 * ```
 *
 * @tutorial indeterminate-flag
 * ```js
 * // indeterminate is a separate flag from checked. Both can be true at once
 * // per HTML spec; the visual just shows the dash.
 * const el = mount('<ry-checkbox indeterminate>Select all</ry-checkbox>');
 * await expectEqual(el.indeterminate, true);
 * await expectAttr(el, 'indeterminate', '');
 * const input = el.querySelector('input');
 * await expectEqual(input.indeterminate, true, 'inner input flag set');
 * await expectEqual(input.getAttribute('aria-checked'), 'mixed', 'aria mixed');
 * ```
 *
 * @tutorial disabled-blocks
 * ```js
 * const el = mount('<ry-checkbox disabled>x</ry-checkbox>');
 * await expectAttr(el, 'disabled', '');
 * const input = el.querySelector('input');
 * await expectEqual(input.disabled, true, 'inner input is also disabled');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange cancels the inner input's click —
 * // the box stays where it was and ry:change never fires.
 * const el = mount('<ry-checkbox>Agree</ry-checkbox>');
 * const input = el.querySelector('input');
 * const changed = [];
 * el.addEventListener('ry:change', () => changed.push(1));
 * el.addEventListener('ry:beforechange', (e) => e.preventDefault(), { once: true });
 * input.click();
 * await expectEqual(el.checked, false, 'vetoed click leaves it unchecked');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * input.click();
 * await expectEqual(el.checked, true, 'without the veto, the click toggles');
 * await expectEqual(changed.length, 1, 'ry:change fired once');
 * ```
 *
 * @see ry-tile-checkbox, ry-switch, ry-radio
 */

import { RyElement } from '../core/ry-element.js';

let checkboxIdCounter = 0;

interface CheckboxChangeDetail {
  value: string;
  checked: boolean;
}

interface CheckboxBeforeChangeDetail extends CheckboxChangeDetail {
  previous: boolean;
}

export class RyCheckbox extends RyElement {
  #input: HTMLInputElement | null = null;

  static observedAttributes = ['checked', 'disabled', 'indeterminate'] as const;

  setup(): void {
    this.#transformMarkup();

    this.#input = this.$<HTMLInputElement>('input[type="checkbox"]');
    if (this.#input) {
      // Apply indeterminate flag at setup (HTML can't express it as an attribute).
      if (this.hasAttribute('indeterminate')) {
        this.#input.indeterminate = true;
        this.#input.setAttribute('aria-checked', 'mixed');
        this.dataset.ryState = 'indeterminate';
      }
      this.on(this.#input, 'click', this.#handleClick);
      this.on(this.#input, 'change', this.#handleChange);
    }
  }

  #transformMarkup(): void {
    // Idempotent — bail if already rendered.
    if (this.$('[data-ry-target="box"]')) return;

    const id = `ry-checkbox-${++checkboxIdCounter}`;
    const name = this.getAttribute('name') ?? '';
    const value = this.getAttribute('value') ?? 'on';
    const checked = this.hasAttribute('checked');
    const disabled = this.hasAttribute('disabled');
    const label = this.textContent?.trim() ?? '';

    // data-ry-target for JS queries, class for CSS styling
    this.innerHTML = `
      <label data-ry-target="wrapper" class="ry-checkbox__wrapper" for="${id}">
        <input
          type="checkbox"
          id="${id}"
          data-ry-target="input"
          class="ry-checkbox__input"
          value="${value}"
          ${name ? `name="${name}"` : ''}
          ${checked ? 'checked' : ''}
          ${disabled ? 'disabled' : ''}
          aria-checked="${checked}"
        >
        <span data-ry-target="box" class="ry-checkbox__box" aria-hidden="true">
          <svg data-ry-target="check" class="ry-checkbox__check" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3.5 8.5 6.5 11.5 12.5 4.5"></polyline>
          </svg>
          <span data-ry-target="dash" class="ry-checkbox__dash"></span>
        </span>
        ${label ? `<span data-ry-target="label" class="ry-checkbox__label">${label}</span>` : ''}
      </label>
    `;
  }

  // The interception point. By the time a checkbox's `click` dispatches, the
  // platform has ALREADY flipped `checked` (and cleared `indeterminate`) as its
  // pre-activation step — so `input.checked` here is the NEXT value. Cancelling
  // the click makes the platform restore both, and no native `change` fires.
  #handleClick = (event: MouseEvent): void => {
    if (!this.#input) return;
    const checked = this.#input.checked;
    if (!this.emit<CheckboxBeforeChangeDetail>('beforechange', {
      value: this.#input.value,
      checked,
      previous: !checked,
    })) event.preventDefault();
  };

  #handleChange = (): void => {
    if (!this.#input) return;

    // A user click on an indeterminate checkbox clears indeterminate and toggles checked.
    if (this.#input.indeterminate) this.#input.indeterminate = false;
    if (this.hasAttribute('indeterminate')) this.removeAttribute('indeterminate');
    if (this.dataset.ryState === 'indeterminate') delete this.dataset.ryState;

    const checked = this.#input.checked;
    this.#input.setAttribute('aria-checked', String(checked));

    if (checked) this.setAttribute('checked', '');
    else this.removeAttribute('checked');

    this.emit<CheckboxChangeDetail>('change', {
      value: this.#input.value,
      checked,
    });
  };

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.#input) return;

    if (name === 'indeterminate') {
      const on = newValue !== null;
      this.#input.indeterminate = on;
      this.#input.setAttribute('aria-checked', on ? 'mixed' : String(this.#input.checked));
      if (on) this.dataset.ryState = 'indeterminate';
      else if (this.dataset.ryState === 'indeterminate') delete this.dataset.ryState;
    } else if (name === 'checked') {
      const on = newValue !== null;
      this.#input.checked = on;
      if (!this.#input.indeterminate) {
        this.#input.setAttribute('aria-checked', String(on));
      }
    } else if (name === 'disabled') {
      this.#input.disabled = newValue !== null;
    }
  }

  get checked(): boolean {
    return this.#input?.checked ?? false;
  }

  set checked(value: boolean) {
    if (!this.#input) return;
    this.#input.checked = value;
    if (!this.#input.indeterminate) {
      this.#input.setAttribute('aria-checked', String(value));
    }
    if (value) this.setAttribute('checked', '');
    else this.removeAttribute('checked');
  }

  get indeterminate(): boolean {
    return this.#input?.indeterminate ?? false;
  }

  set indeterminate(value: boolean) {
    if (!this.#input) return;
    this.#input.indeterminate = value;
    if (value) {
      this.#input.setAttribute('aria-checked', 'mixed');
      this.setAttribute('indeterminate', '');
      this.dataset.ryState = 'indeterminate';
    } else {
      this.#input.setAttribute('aria-checked', String(this.#input.checked));
      this.removeAttribute('indeterminate');
      if (this.dataset.ryState === 'indeterminate') delete this.dataset.ryState;
    }
  }

  get value(): string {
    return this.#input?.value ?? '';
  }

  set value(val: string) {
    if (this.#input) this.#input.value = val;
  }
}

customElements.define('ry-checkbox', RyCheckbox);
